import React, { useState } from "react";
import { IoChevronDownOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FcViewDetails } from "react-icons/fc";

import { SearchIcon, CountryIcon } from "../../../../assets/Icons";
import { Button } from "../../../../components/Reusable/Button";
import ProductPackages from "../../ProductPackage";
import {
  LabelInput,
  DownloadLinkInput,
} from "../../../../components/Reusable/Layout";
import { EditGamerIcon, DeleteIcon } from "../../../../assets/Icons";
import { faq } from "../../../../assets/DemoData";
import FAQModal from "../../Modal/FAQModal";
import EditFAQModal from "../../Modal/EditFAQModal";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  deleteGiftcardPlan,
  fetchGiftcardPlans,
  updateGiftcardPlan,
} from "../../../../features/auth/store/giftcardPlanSlice";
import Modal from "../../../../components/Reusable/Modal";
import { formatCountries } from "../../../../components/Reusable/utils";
import { buildPlanStatusFormData } from "../../statusToggleUtils";
import { StatusSwitch } from "../../../../components/Reusable/ToggleSwitch";
import {
  GiftcardTableProps,
  ProductSearchFilterProps,
  GifcardDetailViewProps,
} from "./types";
import { FiEdit3 } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { Faq } from "../../types";

export const GiftcardTable: React.FC<GiftcardTableProps> = ({
  giftcardPlans,
  statusFilter,
  searchTerm,
  onGiftcardPlanClick,
  onDeleteClick,
  onEditClick,
  loading,
}) => {
  // Pagination state
  const {
    pagination,
    lastPage: totalPages,
    currentPage,
  } = useSelector((state: RootState) => state.giftcardPlans);
  const dispatch = useDispatch<AppDispatch>();
  const [updatingPlanIds, setUpdatingPlanIds] = useState<number[]>([]);
  // Calculate pagination values
  const { nextPageUrl, prevPageUrl } = pagination;

  // Handle page navigation
  const goToNextPage = () => {
    if (currentPage < totalPages && nextPageUrl !== null) {
      dispatch(
        fetchGiftcardPlans({
          url: nextPageUrl,
          status: statusFilter !== "" ? statusFilter : undefined,
          search: searchTerm !== "" ? searchTerm : undefined,
        }),
      );
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1 && prevPageUrl !== null) {
      dispatch(
        fetchGiftcardPlans({
          url: prevPageUrl,
          status: statusFilter !== "" ? statusFilter : undefined,
          search: searchTerm !== "" ? searchTerm : undefined,
        }),
      );
    }
  };

  const handleStatusToggle = async (
    giftcardPlan: (typeof giftcardPlans)[number],
  ) => {
    const nextStatus = giftcardPlan.status === "on" ? "off" : "on";

    setUpdatingPlanIds((prev) => [...prev, giftcardPlan.id]);

    try {
      await dispatch(
        updateGiftcardPlan({
          id: giftcardPlan.id,
          data: buildPlanStatusFormData(giftcardPlan, nextStatus),
        }),
      ).unwrap();
      toast.success(
        `Plan ${nextStatus === "on" ? "activated" : "deactivated"} successfully`,
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update plan status");
    } finally {
      setUpdatingPlanIds((prev) => prev.filter((id) => id !== giftcardPlan.id));
    }
  };
  return (
    <div className="flex flex-col">
      <div className="overflow-x-auto border border-[#FFFFFF1A] rounded-2xl">
        <table className="w-full min-w-[1200px] font-Inter border border-[#FFFFFF1A] rounded-2xl  cousor-pointer">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="px-6 text-left bg-gray-100 dark:bg-[#FFFFFF1A]">
                Status
              </th>
              <th className="px-6 text-left">Provider</th>
              <th className="px-6 text-left">Sub category</th>
              <th className="px-6 text-left">Product Name</th>
              <th className="px-6 text-left">Price Currency</th>
              <th className="px-6 text-left">Price Type</th>
              <th className="px-6 text-left">Wallet Price</th>
              <th className="px-6 text-left">API Price</th>
              <th className="px-6 text-left">ATM Price</th>
              <th className="px-6 text-left">Point Bonus</th>
              <th className="px-6 text-left">Discount Offered</th>
              <th className="px-6 text-left">Source</th>
              <th className="px-6 text-left">Code</th>
              <th className="px-6 text-left">Countries</th>
              <th className="px-6 text-left">Product Image</th>
              <th className="px-6 text-left bg-gray-100 dark:bg-[#FFFFFF1A]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm cursor-pointer">
            {loading ? (
              <tr className="border-b border-gray-200 h-[80px]">
                <td colSpan={16} className="px-6 text-center">
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : giftcardPlans.length > 0 ? (
              giftcardPlans.map((giftcardPlan) => (
                <tr
                  key={giftcardPlan.id}
                  className="border-b border-gray-200 h-[50px] "
                >
                  <td className="px-6 py-4 align-middle">
                    <StatusSwitch
                      checked={giftcardPlan.status === "on"}
                      disabled={updatingPlanIds.includes(giftcardPlan.id)}
                      onChange={() => handleStatusToggle(giftcardPlan)}
                      label={`Toggle ${giftcardPlan.name || "plan"} status`}
                    />
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.category?.provider?.name ||
                      giftcardPlan.provider?.name ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle capitalize">
                    {giftcardPlan.category?.name ||
                      giftcardPlan.sub_category?.name ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.name || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.price_currency?.code ||
                      giftcardPlan.price_currency?.name ||
                      giftcardPlan.price_currency?.symbol ||
                      giftcardPlan.price_currency_id ||
                      "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.price_type || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle capitalize">
                    {giftcardPlan.wallet_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.api_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.atm_price ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.point_bonus ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.discount ?? "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.source || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {giftcardPlan.code || "-"}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    {formatCountries(giftcardPlan.countries)}
                  </td>
                  <td className="px-6 py-4 align-middle">
                    <img
                      src={giftcardPlan.image_url || "/default-product.png"}
                      alt={giftcardPlan.name || "product"}
                      className="size-[40px] object-cover rounded-lg"
                    />
                  </td>
                  <td className="px-6">
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        className="cursor-pointer"
                        onClick={() => onGiftcardPlanClick(giftcardPlan)}
                      >
                        <FcViewDetails size="20" />
                      </button>
                      <button
                        className="text-blue-600"
                        onClick={() => onEditClick(giftcardPlan)}
                      >
                        <FiEdit3 className="text-2xl" />
                      </button>
                      <button
                        className="text-red-600"
                        onClick={() => onDeleteClick(giftcardPlan.id)}
                      >
                        <MdOutlineDelete className="text-2xl" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-200 h-[50px]">
                <td colSpan={16} className="px-6 text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center border-t border-[#FFFFFF1A]">
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={goToPreviousPage}
          disabled={loading || currentPage === 1 || !prevPageUrl}
        >
          Previous
        </button>
        <span className="text-gray-600 dark:text-white text-xs bp:text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>
        <button
          className={`border border-[#D0D5DD] dark:border-[#FFFFFF1A] px-4 py-2 rounded-md text-xs bp:text-sm dark:text-white dark:bg-[#FFFFFF1A] disabled:opacity-50 disabled:cursor-not-allowed`}
          onClick={goToNextPage}
          disabled={
            loading ||
            currentPage === totalPages ||
            totalPages === 0 ||
            !nextPageUrl
          }
        >
          Next
        </button>
      </div>
    </div>
  );
};

export const GiftcardSearchFilter: React.FC<ProductSearchFilterProps> = ({
  onStatusFilter,
  onCreateNewProduct,
  searchTerm,
  onSearchChange,
}) => {
  const [showStatusDropdown, setShowStatusDropdown] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  const statusOptions = ["on", "off"];

  const handleStatusSelect = (status: string) => {
    setSelectedStatus(status);
    onStatusFilter(status);
    setShowStatusDropdown(false);
  };

  const clearStatusFilter = () => {
    setSelectedStatus(null);
    onStatusFilter("");
    setShowStatusDropdown(false);
  };
  return (
    <div className="flex flex-col lg:flex-row lg:justify-between gap-4 mb-4 w-full">
      <div className="relative w-full lg:max-w-[500px]  h-[48px]">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search for a product..."
          className="border p-2 pl-10 rounded-3xl w-full h-[48px] dark:text-white dark:bg-[#FFFFFF1A] bg-[#F4F4F5] dark:border-[#FFFFFF1A]"
        />
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <SearchIcon />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
        <button className="border p-2 bg-[#F4F4F5] flex-1 lg:w-[100px] h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]">
          <div className="flex items-center justify-center gap-2">
            <CountryIcon />
            <IoChevronDownOutline />
          </div>
        </button>

        <button className="border p-2 bg-[#F4F4F5] flex-1 lg:w-[100px] h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]">
          <div className="flex items-center justify-center gap-2">
            <p className="text-sm truncate">Category</p>{" "}
            <IoChevronDownOutline />
          </div>
        </button>

        <div className="relative flex-1 lg:w-[100px]">
          <button
            className="border p-2 bg-[#F4F4F5] w-full h-[48px] rounded-3xl dark:text-white dark:bg-[#FFFFFF1A] dark:border-[#FFFFFF1A]"
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
          >
            <div className="flex items-center gap-2 justify-center">
              <p className="text-sm truncate">{selectedStatus || "Status"}</p>
              <IoChevronDownOutline />
            </div>
          </button>

          {/* Dropdown: Ensure it stays within screen bounds */}
          {showStatusDropdown && (
            <div className="absolute right-0 z-20 mt-1 w-full lg:w-[150px] bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-[#FFFFFF1A] rounded-md shadow-lg">
              {statusOptions.map((status) => (
                <div
                  key={status}
                  className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer"
                  onClick={() => handleStatusSelect(status)}
                >
                  {status}
                </div>
              ))}
              {selectedStatus && (
                <div
                  className="px-4 py-2 text-red-500 hover:bg-gray-100 dark:hover:bg-[#FFFFFF1A] cursor-pointer border-t"
                  onClick={clearStatusFilter}
                >
                  Clear Filter
                </div>
              )}
            </div>
          )}
        </div>

        <Button
          onClick={() => onCreateNewProduct("0")}
          bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
          textColor="text-white"
          borderRadius="rounded-lg"
          className="w-full lg:w-auto md:max-w-[200px] h-12 px-6 flex items-center justify-center"
        >
          New Products
        </Button>
      </div>
    </div>
  );
};

export const GiftcardDetailView: React.FC<GifcardDetailViewProps> = ({
  giftcardPlan,
  onBack,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Controlled values
  const [name, setName] = useState(giftcardPlan.name);
  const [message, setMessage] = useState(giftcardPlan.message);
  const [status, setStatus] = useState(giftcardPlan.status);

  const [toggle, setToggle] = useState<string>("Product Details");
  const tabItems = ["Product Details", "Product Packages"];
  const [fieldSearch, setFieldSearch] = useState("");
  const [countries, setCountries] = useState<string[]>(
    (giftcardPlan.countries ?? []).map((c: any) => String(c)),
  );
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);
  const [isEditFAQModalOpen, setIsEditFAQModalOpen] = useState(false);
  const [faqToEdit, setFaqToEdit] = useState<Faq | undefined>(undefined);
  const [googlePlayLink, setGooglePlayLink] = useState("");
  const [appStoreLink, setAppStoreLink] = useState("");
  if (!giftcardPlan) return null;

  const handleAddField = () => {
    const trimmed = fieldSearch.trim();
    if (trimmed && !countries.includes(trimmed)) {
      setCountries([...countries, trimmed]);
      setFieldSearch("");
    }
  };

  const handleRemoveField = (index: number) => {
    const updated = [...countries];
    updated.splice(index, 1);
    setCountries(updated);
  };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      await dispatch(
        updateGiftcardPlan({
          id: giftcardPlan.id,
          data: {
            name,
            message,
            status,
            countries,
          },
        }),
      ).unwrap();

      toast("Gift card plan updated successfully!");
    } catch (error) {
      toast("Failed to update gift card plan.");
      console.error("Update error:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteGiftcardPlan(giftcardPlan.id)).unwrap();
      toast("Gift card plan deleted successfully.");
      setShowDeleteModal(false);
      onBack(); // Navigate away
      onBack(); // Go back to the previous view
    } catch (error) {
      toast("Failed to delete gift card plan.");
      console.error("Delete error:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusToggle = () => {
    setStatus((prev) => (prev === "on" ? "off" : "on"));
  };

  return (
    <div className="w-full font-satoshi">
      <div className="p-6">
        {/* Header Section */}
        <div className="bg-white mb-6">
          <div className="flex items-center p-6 gap-2">
            <button
              onClick={onBack}
              className="text-gray-600 w-8 h-8 flex items-center justify-center rounded-3xl bg-[#F4F4F5] dark:text-white"
            >
              ←
            </button>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
              {giftcardPlan.name}
            </h2>
            <span
              className={`px-4 py-1 rounded-3xl text-sm ${
                giftcardPlan.status === "on"
                  ? "bg-[#ECFDF3] text-[#067647] border border-[#ABEFC6]"
                  : "bg-[#FFFAEB] text-[#B54708] border border-[#FEDF89]"
              }`}
            >
              {giftcardPlan.status === "on" ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* Tab and Save Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 rounded bg-white p-6 gap-4">
          <div className="flex gap-2">
            {tabItems.map((item, index) => (
              <button
                key={index}
                className={`py-[6px] px-[10px] h-[42px] text-xs flex items-center justify-center ${
                  toggle === item
                    ? "inter-xs-semibold-dark rounded-lg font-inter dark:text-white bg-gray-100 dark:bg-[#FFFFFF1A]"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => setToggle(item)}
              >
                {item}
              </button>
            ))}
          </div>
          <button
            onClick={handleUpdate}
            disabled={isUpdating}
            className="bg-white border border-[#D0D5DD] h-[40px] rounded-lg text-sm font-semibold px-4 py-2"
          >
            {isUpdating ? "Saving..." : "Save changes"}
          </button>
        </div>

        {/* Main Content */}
        {toggle === "Product Details" ? (
          <>
            <div className="bg-white flex flex-col md:flex-row gap-6 p-4 mb-8">
              <div className="bg-[#E6F5FE] dark:bg-[#FFFFFF0A] p-6 rounded-lg flex-1">
                <div className="flex gap-6 justify-between items-center mb-2">
                  <span className="text-sm text-[#475467] dark:text-gray-300">
                    Total purchases of all products
                  </span>
                  <button className="text-gray-400">⋮</button>
                </div>
                <div className="text-xl font-bold text-[#101828] dark:text-white">
                  1,000
                </div>
              </div>
              <div className="bg-[#F6FEF9] dark:bg-[#FFFFFF0A] p-6 rounded-lg flex-1">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-[#475467] dark:text-gray-300">
                    Total Revenue Generated
                  </span>
                  <button className="text-gray-400">⋮</button>
                </div>
                <div className="text-xl font-bold text-[#101828] dark:text-white">
                  ₦5,000,000
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">
                    Product Details
                  </h3>
                  <div className="space-y-4">
                    <LabelInput
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Product Name"
                      name="Product Name"
                    />
                    <LabelInput
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Description"
                      name="Description"
                      className="h-[135px]"
                    />
                  </div>
                </div>

                <div className="bg-white rounded p-4">
                  <p className="text-lg text-[#012030] mb-6 font-semibold border-b border-[#D0D5DD] pb-2">
                    Product FAQ’s
                  </p>
                  {faq.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between items-center py-4 border-b border-[#D0D5DD]"
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-xs">{item.question}</p>
                        <p className="text-[#475467] text-sm">{item.answer}</p>
                      </div>
                      <div className="flex gap-4">
                        <button
                          onClick={() => {
                            setIsEditFAQModalOpen(true);
                            setFaqToEdit(item);
                          }}
                        >
                          <EditGamerIcon />
                        </button>
                        <button>
                          <DeleteIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    className="flex items-center mt-4 gap-2 border border-[#D0D5DD] w-[149px] h-[36px] rounded text-sm font-semibold justify-center"
                    onClick={() => setIsFAQModalOpen(true)}
                  >
                    <span>+</span> Add New FAQ
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Product Images
                  </h3>
                  <img
                    src={giftcardPlan.image_url}
                    alt={giftcardPlan.name}
                    className="w-full h-40 object-cover rounded-lg"
                  />
                </div>

                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Product Availability
                  </h3>
                  <div className="flex justify-between items-center">
                    <span className="text-[#475467] text-base font-inter dark:text-gray-300">
                      Publish product
                    </span>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={status === "on"}
                        onChange={handleStatusToggle}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-500"></div>
                    </label>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Links
                  </h3>
                  <div className="space-y-3">
                    <div className="text-gray-400 text-sm">Download Link</div>
                    <DownloadLinkInput
                      name="googlePlayLink"
                      value={googlePlayLink}
                      onChange={(e) => setGooglePlayLink(e.target.value)}
                      placeholder="Download Link"
                      iconType="google"
                    />
                    <div className="text-gray-400 text-sm">Download Link</div>
                    <DownloadLinkInput
                      name="appStoreLink"
                      value={appStoreLink}
                      onChange={(e) => setAppStoreLink(e.target.value)}
                      placeholder="Download Link"
                      iconType="apple"
                    />
                  </div>
                </div>

                <div className="border border-gray-200 bg-white dark:border-[#FFFFFF1A] rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Countries
                  </h3>
                  <div className="flex gap-2 items-center mb-4">
                    <div className="relative w-full">
                      <input
                        type="text"
                        placeholder="Search for country"
                        value={fieldSearch}
                        onChange={(e) => setFieldSearch(e.target.value)}
                        className="w-full p-2 border rounded bg-white pl-10"
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <SearchIcon />
                      </div>
                    </div>
                    <button
                      onClick={handleAddField}
                      className="w-[150px] font-semibold border border-[#D0D5DD] px-2 py-2 rounded-md text-sm"
                    >
                      Add Country
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {countries.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-4 bg-[#e5f1fa] text-sm px-3 py-2 rounded-full"
                      >
                        {item}
                        <button onClick={() => handleRemoveField(idx)}>
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="text-red-500 text-base w-[152px] h-[48px] bg-[#FEF3F2] font-medium rounded-lg"
              >
                Delete Product
              </button>
            </div>
          </>
        ) : (
          <ProductPackages productId={giftcardPlan.id} />
        )}
      </div>

      {/* Global Modals */}
      <FAQModal
        onCreate={(question, answer) => {
          console.log("New FAQ:", { question, answer });
        }}
        isOpen={isFAQModalOpen}
        onClose={() => setIsFAQModalOpen(false)}
      />
      <EditFAQModal
        onCreate={(question, answer) => {
          console.log("New FAQ:", { question, answer });
        }}
        isOpen={isEditFAQModalOpen && !!faqToEdit}
        onClose={() => {
          setIsEditFAQModalOpen(false);
          setFaqToEdit(undefined);
        }}
        faq={faqToEdit}
      />
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <div className="text-center space-y-4">
          <h2 className="text-lg font-semibold dark:text-white">
            Are you sure you want to delete this product?
          </h2>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-4 py-2 bg-gray-300 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-md"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
